import { Input, Button, SectionLabel } from '@perch/ui';
import { AirportAutocomplete } from '../ui/AirportAutocomplete';
import { SUPPORTED_AIRLINE_OPTIONS } from '../../lib/constants';
import type { BookingFormData, SegmentFormData } from '../../lib/types';

interface ManualEntryFormProps {
  formData: BookingFormData;
  onFormDataChange: (data: BookingFormData) => void;
  onReview: () => void;
  onBack: () => void;
}

const CABIN_CLASSES = [
  { value: 'economy', label: 'Economy' },
  { value: 'premium_economy', label: 'Premium Economy' },
  { value: 'business', label: 'Business' },
  { value: 'first', label: 'First' },
] as const;

/** Auto-infer points program from airline. */
const AIRLINE_PROGRAM: Record<string, string> = {
  AA: 'American AAdvantage',
  DL: 'Delta SkyMiles',
  UA: 'United MileagePlus',
};

function makeEmptySegment(num: number): SegmentFormData {
  return {
    segment_number: num,
    carrier_code: '',
    flight_number: '',
    origin: '',
    destination: '',
    departure_datetime: '',
    arrival_datetime: '',
    cabin_code: 'Y',
    booking_class: 'Y',
  };
}

/**
 * Convert a stored ISO / partial datetime string into the value format
 * that an <input type="datetime-local"> element expects: "YYYY-MM-DDTHH:MM".
 *
 * The input element refuses any value with seconds or a timezone offset
 * and just silently renders empty, which looks like a bug to users when
 * they come back to a form they half-filled. This helper strips the
 * trailing seconds/offset if present and passes through the already-clean
 * prefix otherwise.
 */
function toLocalInputValue(stored: string): string {
  if (!stored) return '';
  // Already in the expected shape (from a prior onChange on this same input).
  const match = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})/.exec(stored);
  if (match) return match[1];
  // Fallback: reparse. Used when seeding from ISO with Z/offset (e.g. a
  // parsed email confirmation that was then routed to manual edit).
  const d = new Date(stored);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Pass-through for datetime-local input values. Kept as a named helper
 * so the rounding/normalization rules live in one place if we ever need
 * to append ":00" seconds or similar.
 */
function fromLocalInputValue(value: string): string {
  return value;
}

/**
 * Simplified manual entry form.
 *
 * Core fields (always visible):
 *   - Confirmation Code (PNR)
 *   - Airline (dropdown)
 *   - Cash/Award toggle (segmented control, not buried in accordion)
 *   - Price Paid (cash) or Points Used + Taxes (award)
 *
 * Per segment (5 fields):
 *   - Carrier Code + Flight Number
 *   - From (airport autocomplete) + To (airport autocomplete)
 *   - Departure Date
 *
 * Advanced (hidden toggle):
 *   - Cabin Class
 */
export function ManualEntryForm({
  formData,
  onFormDataChange,
  onReview,
  onBack,
}: ManualEntryFormProps) {


  // Ensure at least one segment
  if (formData.segments.length === 0) {
    onFormDataChange({ ...formData, segments: [makeEmptySegment(1)] });
  }

  const toggleAward = (isAward: boolean) => {
    const program = isAward ? AIRLINE_PROGRAM[formData.airline_code] : undefined;
    onFormDataChange({
      ...formData,
      is_award_booking: isAward,
      points_program: program,
      points_used: isAward ? formData.points_used : undefined,
      points_currency: isAward ? 'miles' : undefined,
    });
  };

  const addSegment = () => {
    onFormDataChange({
      ...formData,
      segments: [...formData.segments, makeEmptySegment(formData.segments.length + 1)],
    });
  };

  const removeSegment = (index: number) => {
    const newSegments = formData.segments.filter((_, i) => i !== index);
    onFormDataChange({
      ...formData,
      segments: newSegments.map((seg, i) => ({ ...seg, segment_number: i + 1 })),
    });
  };

  const updateSegment = (index: number, field: keyof SegmentFormData, value: string | number) => {
    const newSegments = [...formData.segments];
    newSegments[index] = { ...newSegments[index], [field]: value };
    onFormDataChange({ ...formData, segments: newSegments });
  };

  const hasPricing = formData.is_award_booking
    ? (formData.points_used ?? 0) > 0
    : formData.total_price_paid > 0;

  // Validation predicate matches the server-side segment-validation.ts
  // gate in supabase/functions/_shared/segment-validation.ts: both
  // datetimes required and arrival must be strictly greater than
  // departure. The invalid-segment computation is broken out so we can
  // show an inline hint to the user instead of a silent disabled button.
  const firstInvalidSegmentIdx = formData.segments.findIndex((s) => {
    if (!s.departure_datetime || !s.arrival_datetime) return true;
    const dep = new Date(s.departure_datetime).getTime();
    const arr = new Date(s.arrival_datetime).getTime();
    if (Number.isNaN(dep) || Number.isNaN(arr)) return true;
    return arr <= dep;
  });
  const datetimesValid = firstInvalidSegmentIdx === -1;

  const canReview = formData.pnr.length >= 5
    && formData.airline_code
    && hasPricing
    && formData.segments.length > 0
    && formData.segments.every(s => s.origin && s.destination && s.carrier_code && s.flight_number)
    && datetimesValid;

  return (
    <div className="space-y-4">
      {/* PNR */}
      <Input
        label="Confirmation Code"
        required
        maxLength={6}
        value={formData.pnr}
        onChange={(e) => onFormDataChange({ ...formData, pnr: e.target.value.toUpperCase() })}
        placeholder="ABC123"
      />

      {/* Airline */}
      <div>
        <label className="block mb-2 text-sm font-medium text-stone-700 dark:text-stone-300">
          Airline <span className="text-primary">*</span>
        </label>
        <select
          required
          className="w-full px-4 py-2.5 border border-stone-300 dark:border-stone-600 rounded-lg bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-[border-color,box-shadow]"
          value={formData.airline_code}
          onChange={(e) => {
            const code = e.target.value;
            const program = formData.is_award_booking ? AIRLINE_PROGRAM[code] : undefined;
            onFormDataChange({ ...formData, airline_code: code, points_program: program || formData.points_program });
          }}
        >
          <option value="">Select...</option>
          {SUPPORTED_AIRLINE_OPTIONS.map(a => (
            <option key={a.code} value={a.code}>{a.name} ({a.code})</option>
          ))}
        </select>
      </div>

      {/* Cash / Award toggle — flat segmented control */}
      <div>
        <label className="block mb-2 text-sm font-medium text-stone-700 dark:text-stone-300">
          Booking Type
        </label>
        <div className="flex rounded-lg border border-stone-300 dark:border-stone-600 overflow-hidden" role="radiogroup">
          <button
            role="radio"
            aria-checked={!formData.is_award_booking}
            onClick={() => toggleAward(false)}
            className={`flex-1 py-2 text-sm font-medium transition-colors ${
              !formData.is_award_booking
                ? 'bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900'
                : 'bg-white dark:bg-stone-800 text-stone-500 dark:text-stone-400 hover:text-stone-700 dark:hover:text-stone-300'
            }`}
          >
            Cash
          </button>
          <button
            role="radio"
            aria-checked={formData.is_award_booking}
            onClick={() => toggleAward(true)}
            className={`flex-1 py-2 text-sm font-medium transition-colors ${
              formData.is_award_booking
                ? 'bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900'
                : 'bg-white dark:bg-stone-800 text-stone-500 dark:text-stone-400 hover:text-stone-700 dark:hover:text-stone-300'
            }`}
          >
            Award
          </button>
        </div>
      </div>

      {/* Price fields — different layout for cash vs award */}
      {formData.is_award_booking ? (
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Points Used"
            type="number"
            required
            value={formData.points_used || ''}
            onChange={(e) => onFormDataChange({ ...formData, points_used: e.target.value ? parseInt(e.target.value) : undefined })}
            placeholder="25000"
            helperText={formData.points_program || undefined}
          />
          <Input
            label="Taxes/Fees"
            type="number"
            value={formData.total_price_paid || ''}
            onChange={(e) => onFormDataChange({ ...formData, total_price_paid: parseFloat(e.target.value) || 0 })}
            placeholder="5.60"
            helperText="Optional. $0 if none."
          />
        </div>
      ) : (
        <Input
          label="Price Paid"
          type="number"
          required
          value={formData.total_price_paid || ''}
          onChange={(e) => onFormDataChange({ ...formData, total_price_paid: parseFloat(e.target.value) || 0 })}
          placeholder="450"
          helperText="Used to detect price drops."
        />
      )}

      {/* Award program note — inline with points field, not a floating label */}

      {/* Segments */}
      <div className="space-y-3">
        <SectionLabel>Flights</SectionLabel>

        {formData.segments.map((segment, index) => (
          <div key={index} className="p-3 bg-stone-50 dark:bg-stone-800/50 rounded-xl border border-stone-200/60 dark:border-stone-700/50 space-y-3">
            <div className="flex justify-between items-center">
              <h4 className="text-sm font-medium text-stone-900 dark:text-stone-100">
                Flight {index + 1}
              </h4>
              {formData.segments.length > 1 && (
                <button
                  onClick={() => removeSegment(index)}
                  className="text-xs text-stone-400 hover:text-red-500 transition-colors"
                >
                  Remove
                </button>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Carrier"
                required
                maxLength={2}
                value={segment.carrier_code}
                onChange={(e) => updateSegment(index, 'carrier_code', e.target.value.toUpperCase())}
                placeholder="DL"
              />
              <Input
                label="Flight #"
                required
                value={segment.flight_number}
                onChange={(e) => updateSegment(index, 'flight_number', e.target.value)}
                placeholder="100"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <AirportAutocomplete
                label="From"
                value={segment.origin}
                onChange={(code) => updateSegment(index, 'origin', code)}
                placeholder="JFK"
                required
              />
              <AirportAutocomplete
                label="To"
                value={segment.destination}
                onChange={(code) => updateSegment(index, 'destination', code)}
                placeholder="LAX"
                required
              />
            </div>

            {/*
              Both datetime inputs are required. Previously only a
              departure date was collected and arrival was silently
              defaulted to match, which produced
              `arrival_datetime == departure_datetime` and tripped the
              Postgres `valid_datetime` CHECK constraint on insert
              (Sentry PERCH-EDGE-FUNCTIONS-1C, 3 users, 2026-04-09/10).

              Timezone caveat: `datetime-local` values are interpreted
              in the browser's local timezone when passed to `new Date()`
              and later serialized via `toISOString()`. That is an
              approximation for manual entries — a California user
              entering a JFK flight time will store an instant offset
              by the PT-vs-ET delta. It is accurate enough for the DB
              CHECK (ordering is preserved) and matches the existing
              behavior of the date-only field. Correct per-airport
              timezone resolution is a follow-up (track via IATA -> IANA
              mapping or require explicit offsets in the future).
            */}
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Departure"
                type="datetime-local"
                required
                value={toLocalInputValue(segment.departure_datetime)}
                onChange={(e) => updateSegment(index, 'departure_datetime', fromLocalInputValue(e.target.value))}
                helperText="Local time at origin airport"
              />
              <Input
                label="Arrival"
                type="datetime-local"
                required
                value={toLocalInputValue(segment.arrival_datetime)}
                onChange={(e) => updateSegment(index, 'arrival_datetime', fromLocalInputValue(e.target.value))}
                helperText="Local time at destination"
              />
            </div>
            {firstInvalidSegmentIdx === index && segment.departure_datetime && segment.arrival_datetime && (
              <p className="text-xs text-red-600 dark:text-red-400 -mt-1">
                Arrival must be after departure. For overnight flights, use the next day's date.
              </p>
            )}
          </div>
        ))}

        <button
          onClick={addSegment}
          className="w-full py-2.5 text-sm text-stone-500 dark:text-stone-400 border border-dashed border-stone-300 dark:border-stone-600 rounded-xl hover:border-stone-400 dark:hover:border-stone-500 hover:text-stone-700 dark:hover:text-stone-300 transition-colors"
        >
          + Add Another Flight
        </button>
      </div>

      {/* Cabin class */}
      <div>
        <label className="block mb-2 text-sm font-medium text-stone-700 dark:text-stone-300">Cabin Class</label>
        <select
          className="w-full px-4 py-2.5 border border-stone-300 dark:border-stone-600 rounded-lg bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-[border-color,box-shadow]"
          value={formData.cabin_class}
          onChange={(e) => onFormDataChange({ ...formData, cabin_class: e.target.value })}
        >
          {CABIN_CLASSES.map(c => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
      </div>

      {/* Basic economy / non-changeable fare warning */}
      {formData.cabin_class === 'economy' && (
        <label className="flex items-start gap-2.5 text-sm text-stone-600 dark:text-stone-400">
          <input
            type="checkbox"
            checked={formData.is_changeable === false}
            onChange={(e) => onFormDataChange({ ...formData, is_changeable: e.target.checked ? false : undefined })}
            className="w-4 h-4 mt-0.5 rounded border-stone-300 dark:border-stone-600 text-primary focus:ring-primary/20"
          />
          <span>
            <span className="font-medium text-stone-700 dark:text-stone-300">Basic Economy</span>
            <span className="block text-xs text-stone-400 dark:text-stone-500 mt-0.5">
              Basic economy fares typically can't be changed. We'll still watch the price, but rebooking may not be possible.
            </span>
          </span>
        </label>
      )}

      {/* Actions */}
      <div className="flex gap-3 pt-2">
        <Button variant="outline" onClick={onBack} fullWidth>
          Cancel
        </Button>
        <Button
          variant="coral"
          onClick={onReview}
          fullWidth
          disabled={!canReview}
        >
          Review
        </Button>
      </div>
    </div>
  );
}
